/**
 * risk/portfolioRisk.ts
 *
 * Portfolio-aware risk layer for MER Algo Footprints v4.3+
 *
 * Symbol-level risk answers: "Is this a good trade?"
 * Portfolio risk answers:    "Is this a good trade GIVEN everything else on the board?"
 *
 * Concepts:
 *   Portfolio heat     — too many high-readiness setups simultaneously
 *   Bias imbalance     — too many longs vs shorts
 *   Sector clustering  — over-concentration in one group (semis, big tech, etc.)
 *   Conflict detection — setups that directly oppose each other (long SPY + short QQQ)
 *
 * Integration:
 *   DecisionDashboard calls buildPortfolioState() once per render cycle,
 *   then passes it to evaluatePortfolioRisk() for each setup card.
 *   Portfolio adjustments modify readiness scores and tiers.
 */

import { TradeSetup } from '../../../types';
import { SymbolContext } from '../../../services/buildSymbolContext';
import { RiskAssessment, ReadinessTier, evaluateSetupRisk } from './riskEngine';

// ─── Correlation groups ───────────────────────────────────────────────────────
// Heuristic clusters — not quantitative. Symbols in the same group are treated
// as correlated for concentration risk purposes.

const CORRELATION_GROUPS: Record<string, string[]> = {
  broad_market:   ['SPY', 'QQQ', 'IWM', 'DIA', 'VOO', 'VTI'],
  semiconductors: ['NVDA', 'AMD', 'INTC', 'MU', 'AVGO', 'QCOM', 'TSM', 'ASML', 'SOXX', 'SMH'],
  big_tech:       ['AAPL', 'MSFT', 'GOOGL', 'GOOG', 'META', 'AMZN'],
  ai_plays:       ['NVDA', 'AMD', 'PLTR', 'MSFT', 'GOOGL', 'SMCI'],
  ev_auto:        ['TSLA', 'RIVN', 'LCID', 'F', 'GM', 'NIO'],
  fintech:        ['SQ', 'PYPL', 'AFRM', 'SOFI', 'COIN'],
};

// Build reverse map: symbol → group names
const GROUP_MAP = new Map<string, string[]>();
for (const [group, syms] of Object.entries(CORRELATION_GROUPS)) {
  for (const s of syms) {
    const existing = GROUP_MAP.get(s) ?? [];
    existing.push(group);
    GROUP_MAP.set(s, existing);
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type PortfolioHeat = 'cool' | 'balanced' | 'elevated' | 'crowded' | 'overloaded';

export interface ActiveSetupEntry {
  symbol:    string;
  setup:     TradeSetup;
  risk:      RiskAssessment;
  readiness: ReadinessTier;
}

export interface SectorCluster {
  group:   string;
  symbols: string[];
  warning: string;
}

export interface ConflictPair {
  symbolA: string;
  dirA:    string;
  symbolB: string;
  dirB:    string;
  reason:  string;
}

// ─── Optional position state ─────────────────────────────────────────────────
// When real positions are available, portfolio logic uses them for exact exposure.
// When absent, the system falls back to heuristic opportunity-board analysis.
// This preserves all current behavior unchanged.

export interface PositionEntry {
  symbol:       string;
  dir:          'long' | 'short';
  size:         number;           // number of shares / contracts
  entryPrice:   number;
  currentPrice: number;
  dollarExposure: number;         // size × currentPrice
  sector?:      string;           // optional — used for concentration if provided
}

export interface PositionState {
  positions:       PositionEntry[];
  totalLongExposure:  number;
  totalShortExposure: number;
  totalExposure:      number;
}

/** Build a PositionState from a simple positions array */
export function buildPositionState(positions: PositionEntry[]): PositionState {
  const longs  = positions.filter(p => p.dir === 'long');
  const shorts = positions.filter(p => p.dir === 'short');
  return {
    positions,
    totalLongExposure:  longs.reduce((s, p) => s + p.dollarExposure, 0),
    totalShortExposure: shorts.reduce((s, p) => s + p.dollarExposure, 0),
    totalExposure:      positions.reduce((s, p) => s + p.dollarExposure, 0),
  };
}

export interface RedundancyGroup {
  theme:   string;
  symbols: string[];
  dirs:    string[];
  note:    string;    // plain-English: 'Multiple semiconductor longs — overlapping exposure'
}

export interface PortfolioState {
  activeSymbols:       string[];
  activeSetups:        ActiveSetupEntry[];
  longCount:           number;
  shortCount:          number;
  biasRatio:           number;        // longCount / total, 0.5 = balanced
  urgentCount:         number;
  heatLevel:           PortfolioHeat;
  sectorClusters:      SectorCluster[];
  conflicts:           ConflictPair[];
  redundancyGroups:    RedundancyGroup[];   // similar trades in disguised form
  netDirectionalBias:  'long-heavy' | 'short-heavy' | 'balanced';
  concentrationScore:  number;             // 0–100, higher = more concentrated
}

export interface PortfolioRiskResult {
  portfolioConflict:      boolean;
  portfolioNote:          string;
  adjustedReadinessScore: number;
  adjustedTier:           ReadinessTier;
  heatPenalty:            number;
  clusterPenalty:         number;
  conflictPenalty:        number;
}

// Extends the base RiskAssessment with portfolio fields
export interface FullRiskAssessment extends RiskAssessment {
  // Standalone (symbol-level only, before portfolio context)
  standaloneReadinessScore: number;
  standaloneTier:           ReadinessTier;

  // Portfolio-adjusted
  portfolioConflict:        boolean;
  portfolioNote:            string;
  adjustedReadinessScore:   number;
  adjustedTier:             ReadinessTier;
  heatPenalty:              number;
  clusterPenalty:           number;
  conflictPenalty:          number;
  redundancyNote:           string;   // if setup is redundant with another on the board
}

// ─── Build portfolio state ─────────────────────────────────────────────────────

export function buildPortfolioState(
  contexts:      Record<string, SymbolContext>,
  aiConvictions: Record<string, number> = {},
  positions?:    PositionState | null      // optional — uses real exposure when provided
): PortfolioState {
  // When real positions are provided, enrich bias counts from actual holdings
  // rather than from the opportunity board. Heuristic fallback when absent.
  const activeSymbols = Object.keys(contexts);
  const activeSetups: ActiveSetupEntry[] = [];

  for (const [symbol, ctx] of Object.entries(contexts)) {
    const conviction = aiConvictions[symbol] ?? 0;
    for (const setup of ctx.setups) {
      const risk = evaluateSetupRisk(setup, ctx, conviction);
      if (risk.readiness === 'good' || risk.readiness === 'ready' || risk.readiness === 'urgent') {
        activeSetups.push({ symbol, setup, risk, readiness: risk.readiness });
      }
    }
  }

  // Start with heuristic board counts
  let longCount  = activeSetups.filter(e => e.setup.dir === 'long').length;
  let shortCount = activeSetups.filter(e => e.setup.dir === 'short').length;

  // If real positions available, override with actual long/short position counts
  if (positions?.positions?.length) {
    longCount  = positions.positions.filter(p => p.dir === 'long').length;
    shortCount = positions.positions.filter(p => p.dir === 'short').length;
  }

  const total     = longCount + shortCount;
  const biasRatio = total > 0 ? longCount / total : 0.5;
  const urgentCount = activeSetups.filter(e => e.readiness === 'urgent').length;

  const heatLevel: PortfolioHeat =
    urgentCount >= 5 || activeSetups.length >= 8 ? 'overloaded' :
    urgentCount >= 3 || activeSetups.length >= 6 ? 'crowded'    :
    urgentCount >= 2 || activeSetups.length >= 4 ? 'elevated'   :
    activeSetups.length >= 2                     ? 'balanced'   : 'cool';

  const longH  = longCount > 0 && shortCount === 0 ? 'long-heavy'
    : shortCount > 0 && longCount === 0 ? 'short-heavy'
    : biasRatio > 0.7 ? 'long-heavy'
    : biasRatio < 0.3 ? 'short-heavy' : 'balanced';

  const clusters = detectSectorClusters(activeSetups);
  const concentrationScore = Math.min(100, clusters.reduce((s, c) => s + c.symbols.length * 15, 0));

  return {
    activeSymbols,
    activeSetups,
    longCount,
    shortCount,
    biasRatio,
    urgentCount,
    heatLevel,
    sectorClusters:     clusters,
    conflicts:          detectConflicts(activeSetups),
    redundancyGroups:   detectRedundancyGroups(activeSetups),
    netDirectionalBias: longH,
    concentrationScore,
  };
}

// ─── Evaluate portfolio risk for a single setup ────────────────────────────────

export function evaluatePortfolioRisk(
  symbol:    string,
  setup:     TradeSetup,
  baseRisk:  RiskAssessment,
  portfolio: PortfolioState
): PortfolioRiskResult {
  const notes: string[] = [];
  let heatPenalty     = 0;
  let clusterPenalty  = 0;
  let conflictPenalty = 0;
  let portfolioConflict = false;

  // ── Portfolio heat ────────────────────────────────────────────────────────
  if (portfolio.heatLevel === 'overloaded') {
    heatPenalty = 20;
    notes.push(`Portfolio overloaded (${portfolio.activeSetups.length} active setups) — be highly selective`);
  } else if (portfolio.heatLevel === 'crowded') {
    heatPenalty = 10;
    notes.push(`Portfolio crowded (${portfolio.urgentCount} urgent setups active)`);
  } else if (portfolio.heatLevel === 'elevated') {
    heatPenalty = 4;
    notes.push('Portfolio elevated — monitor concentration');
  }

  // ── Bias imbalance ────────────────────────────────────────────────────────
  if (setup.dir === 'long' && portfolio.biasRatio > 0.8 && portfolio.longCount >= 4) {
    heatPenalty += 8;
    notes.push(`Long-heavy board (${portfolio.longCount}L / ${portfolio.shortCount}S) — adding more longs increases directional risk`);
  } else if (setup.dir === 'short' && portfolio.biasRatio < 0.2 && portfolio.shortCount >= 4) {
    heatPenalty += 8;
    notes.push(`Short-heavy board (${portfolio.longCount}L / ${portfolio.shortCount}S) — adding more shorts increases directional risk`);
  }

  // ── Sector clustering ─────────────────────────────────────────────────────
  const symGroups = GROUP_MAP.get(symbol.toUpperCase()) ?? [];
  for (const cluster of portfolio.sectorClusters) {
    if (symGroups.includes(cluster.group) && cluster.symbols.length >= 2) {
      clusterPenalty = Math.max(clusterPenalty, 12);
      notes.push(`${cluster.group} cluster: ${cluster.symbols.join(', ')} already active — concentration risk`);
    }
  }

  // ── Conflict detection ────────────────────────────────────────────────────
  for (const conflict of portfolio.conflicts) {
    if (conflict.symbolA === symbol || conflict.symbolB === symbol) {
      conflictPenalty = Math.max(conflictPenalty, 18);
      portfolioConflict = true;
      notes.push(`Conflict: ${conflict.reason}`);
    }
  }

  // ── Adjusted score and tier ───────────────────────────────────────────────
  const total = heatPenalty + clusterPenalty + conflictPenalty;
  const adjustedScore = Math.max(0, baseRisk.readinessScore - total);

  const adjustedTier: ReadinessTier =
    adjustedScore >= 80 ? 'urgent'      :
    adjustedScore >= 65 ? 'ready'       :
    adjustedScore >= 45 ? 'good'        : 'interesting';

  return {
    portfolioConflict,
    portfolioNote: notes.length > 0 ? notes.join(' · ') : 'No portfolio conflicts.',
    adjustedReadinessScore: adjustedScore,
    adjustedTier,
    heatPenalty,
    clusterPenalty,
    conflictPenalty,
  };
}

// ─── Full evaluation (base + portfolio) ──────────────────────────────────────

export function evaluateSetupWithPortfolio(
  symbol:       string,
  setup:        TradeSetup,
  ctx:          SymbolContext,
  portfolio:    PortfolioState,
  aiConviction: number = 0
): FullRiskAssessment {
  const base     = evaluateSetupRisk(setup, ctx, aiConviction);
  const portRisk = evaluatePortfolioRisk(symbol, setup, base, portfolio);

  // Redundancy note: check if another setup on the board is in the same theme/direction
  const redundancyNote = buildRedundancyNote(symbol, setup, portfolio);

  return {
    ...base,
    standaloneReadinessScore: base.readinessScore,
    standaloneTier:           base.readiness,
    ...portRisk,
    redundancyNote,
  };
}

function buildRedundancyNote(
  symbol: string, setup: TradeSetup, portfolio: PortfolioState
): string {
  for (const rg of portfolio.redundancyGroups) {
    if (rg.symbols.includes(symbol.toUpperCase())) {
      const others = rg.symbols.filter(s => s !== symbol.toUpperCase());
      if (others.length) {
        return `Redundant with ${others.join(', ')} (${rg.theme}) — overlapping ${setup.dir} exposure`;
      }
    }
  }
  return '';
}

// ─── Portfolio summary (for display) ─────────────────────────────────────────

export interface PortfolioSummary {
  heat:              PortfolioHeat;
  heatColor:         string;
  heatLabel:         string;
  longCount:         number;
  shortCount:        number;
  urgentCount:       number;
  clusterCount:      number;
  conflictCount:     number;
  redundancyCount:   number;
  concentrationScore: number;
  netDirectionalBias: string;
  note:              string;
}

export const HEAT_COLORS: Record<PortfolioHeat, string> = {
  cool:      'var(--blue-dim)',
  balanced:  'var(--green)',
  elevated:  'var(--cyan)',
  crowded:   'var(--amber)',
  overloaded:'var(--red)',
};

export const HEAT_LABELS: Record<PortfolioHeat, string> = {
  cool:      '❄ COOL',
  balanced:  '◎ BALANCED',
  elevated:  '◑ ELEVATED',
  crowded:   '▲ CROWDED',
  overloaded:'⚠ OVERLOADED',
};

export function buildPortfolioSummary(portfolio: PortfolioState): PortfolioSummary {
  const notes: string[] = [];
  if (portfolio.heatLevel === 'overloaded') notes.push('Too many simultaneous opportunities — be highly selective');
  else if (portfolio.heatLevel === 'crowded') notes.push('Board is crowded — prioritize highest-conviction setups');
  else if (portfolio.heatLevel === 'elevated') notes.push('Above-average activity — monitor concentration');
  if (portfolio.biasRatio > 0.75)           notes.push(`Long-heavy (${portfolio.longCount}L/${portfolio.shortCount}S)`);
  if (portfolio.biasRatio < 0.25)           notes.push(`Short-heavy (${portfolio.longCount}L/${portfolio.shortCount}S)`);
  if (portfolio.sectorClusters.length)      notes.push(`${portfolio.sectorClusters.length} sector cluster(s)`);
  if (portfolio.conflicts.length)           notes.push(`${portfolio.conflicts.length} directional conflict(s)`);

  return {
    heat:               portfolio.heatLevel,
    heatColor:          HEAT_COLORS[portfolio.heatLevel],
    heatLabel:          HEAT_LABELS[portfolio.heatLevel],
    longCount:          portfolio.longCount,
    shortCount:         portfolio.shortCount,
    urgentCount:        portfolio.urgentCount,
    clusterCount:       portfolio.sectorClusters.length,
    conflictCount:      portfolio.conflicts.length,
    redundancyCount:    portfolio.redundancyGroups.length,
    concentrationScore: portfolio.concentrationScore,
    netDirectionalBias: portfolio.netDirectionalBias,
    note:               notes.join(' · ') || 'Portfolio balanced.',
  };
}

// ─── Private helpers ──────────────────────────────────────────────────────────

function detectRedundancyGroups(setups: ActiveSetupEntry[]): RedundancyGroup[] {
  // Redundancy = same theme, same direction (different symbols but same bet)
  const groups: Record<string, { symbols: string[]; dirs: string[] }> = {};
  for (const entry of setups) {
    const sym = entry.symbol.toUpperCase();
    const dir = entry.setup.dir;
    for (const [theme, members] of Object.entries(CORRELATION_GROUPS)) {
      if (members.includes(sym)) {
        const key = `${theme}:${dir}`;
        if (!groups[key]) groups[key] = { symbols: [], dirs: [] };
        if (!groups[key].symbols.includes(sym)) {
          groups[key].symbols.push(sym);
          groups[key].dirs.push(dir);
        }
      }
    }
  }
  return Object.entries(groups)
    .filter(([, v]) => v.symbols.length >= 2)
    .map(([key, v]) => {
      const [theme, dir] = key.split(':');
      return {
        theme: theme ?? key,
        symbols: v.symbols,
        dirs:    v.dirs,
        note:    `Multiple ${dir} setups in ${theme} — overlapping directional exposure`,
      };
    });
}

function detectSectorClusters(setups: ActiveSetupEntry[]): SectorCluster[] {
  const groups: Record<string, string[]> = {};
  for (const entry of setups) {
    const sym = entry.symbol.toUpperCase();
    for (const g of GROUP_MAP.get(sym) ?? []) {
      if (!groups[g]) groups[g] = [];
      if (!groups[g].includes(sym)) groups[g].push(sym);
    }
  }
  return Object.entries(groups)
    .filter(([, syms]) => syms.length >= 2)
    .map(([group, syms]) => ({
      group, symbols: syms,
      warning: `${syms.length} correlated names active (${group})`,
    }));
}

function detectConflicts(setups: ActiveSetupEntry[]): ConflictPair[] {
  const conflicts: ConflictPair[] = [];
  const broadMarket = CORRELATION_GROUPS['broad_market'];

  // Long + Short in the same broad-market cluster
  const longBroad  = setups.filter(e => e.setup.dir === 'long'  && broadMarket.includes(e.symbol.toUpperCase()));
  const shortBroad = setups.filter(e => e.setup.dir === 'short' && broadMarket.includes(e.symbol.toUpperCase()));
  for (const l of longBroad) {
    for (const s of shortBroad) {
      conflicts.push({
        symbolA: l.symbol, dirA: 'long',
        symbolB: s.symbol, dirB: 'short',
        reason: `Long ${l.symbol} vs Short ${s.symbol} — correlated broad-market instruments`,
      });
    }
  }

  // Same symbol, opposite directions
  const bySymbol: Record<string, string[]> = {};
  for (const e of setups) {
    if (!bySymbol[e.symbol]) bySymbol[e.symbol] = [];
    bySymbol[e.symbol].push(e.setup.dir);
  }
  for (const [sym, dirs] of Object.entries(bySymbol)) {
    if (dirs.includes('long') && dirs.includes('short')) {
      conflicts.push({
        symbolA: sym, dirA: 'long',
        symbolB: sym, dirB: 'short',
        reason: `Conflicting long and short setups on ${sym}`,
      });
    }
  }

  return conflicts;
}
